<template>
  <div
    class="embedded-pool h-[100vh] w-full overflow-hidden p-4"
    :class="{ 'embedded-pool--fullscreen': fullscreen }"
  >
    <div
      v-for="entry in orchestrator.cachedEmbeddedApps"
      v-show="orchestrator.activeEmbeddedKey === entry.key"
      :key="entry.key"
      class="embedded-iframe-wrapper absolute overflow-hidden"
      :class="[
        { 'z-10': orchestrator.activeEmbeddedKey === entry.key },
        fullscreen ? 'inset-0' : 'inset-4',
      ]"
    >
      <iframe
        :src="entry.url"
        class="embedded-iframe w-full border-0"
      />
    </div>
    <!-- Loading state while fetching app config for current route -->
    <div
      v-if="loading"
      class="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 rounded-lg"
    >
      <div
        class="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"
      />
      <p class="text-gray-500 text-sm">
        Loading application...
      </p>
    </div>
    <!-- No app found: we requested this route's app but it had no valid URL -->
    <div
      v-else-if="
        currentRequestedKey && !loading && !orchestrator.activeEmbedded
      "
      class="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-gray-50 rounded-lg"
    >
      <div class="text-4xl mb-4">
        ⚠️
      </div>
      <h2 class="text-xl font-semibold text-gray-800">
        No Application Found
      </h2>
      <p class="text-gray-500 mt-2 max-w-md">
        This application does not have a valid URL. Please contact the
        administrator or check the side navigation configuration.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from "vue";
import { useRoute } from "vue-router";
import http from "../services/http";
import { useAppOrchestratorStore } from "../store/appOrchestrator";

/* eslint-disable-next-line no-undef -- Vue 3 script setup compiler macro */
defineProps({
  fullscreen: { type: Boolean, default: false },
});

const EMBEDDED_ROUTE_NAMES = [
  "CustomAppBySectionAndSlug",
  "CustomAppBySlug",
  "CustomAppDirectSlug",
  "CustomAppById",
];

type AppRecord = {
  id: number;
  label: string;
  route: string;
  iframe_url: string;
};

const route = useRoute();
const orchestrator = useAppOrchestratorStore();
const loading = ref(false);
/** Key we're currently loading; used to show "No Application Found" when load fails. */
const currentRequestedKey = ref<string | null>(null);

function isLoopbackHost(value: string): boolean {
  const normalized = value.trim().toLowerCase();

  return (
    normalized === "localhost" ||
    normalized === "127.0.0.1" ||
    normalized === "::1"
  );
}

function resolveLocalIframeOrigin(): string {
  const configured = String(
    process.env.VUE_APP_LOCAL_IFRAME_ORIGIN || ""
  ).trim();

  if (configured) {
    return configured;
  }

  return "http://localhost:5173";
}

function rewriteIframeUrlForLocalDev(url: string): string {
  if (!url) {
    return "";
  }

  if (process.env.NODE_ENV !== "development") {
    return url;
  }

  try {
    const resolvedUrl = new URL(url);
    const currentHost = window.location.hostname.trim().toLowerCase();

    if (isLoopbackHost(currentHost) && !isLoopbackHost(resolvedUrl.hostname)) {
      const localOrigin = resolveLocalIframeOrigin();
      const localUrl = new URL(localOrigin);
      resolvedUrl.protocol = localUrl.protocol;
      resolvedUrl.host = localUrl.host;
    }

    return resolvedUrl.toString();
  } catch {
    return url;
  }
}

/**
 * Tell embedded microapps exactly which window owns the SSO session. Relying
 * only on document.referrer/ancestorOrigins is not reliable with restrictive
 * referrer policies, and causes the iframe to never receive SSO_GRANT.
 */
function appendParentOrigin(url: string): string {
  try {
    const iframeUrl = new URL(url, window.location.origin);
    iframeUrl.searchParams.set("parent_origin", window.location.origin);
    return iframeUrl.toString();
  } catch {
    return url;
  }
}

function isEmbeddedRoute(): boolean {
  return !!route.name && EMBEDDED_ROUTE_NAMES.includes(route.name as string);
}

function getPoolKey(): string | null {
  const params = route.params as { slug?: string; id?: string };
  if (params.slug) return params.slug;
  if (params.id) return `id:${params.id}`;
  return null;
}

async function loadAndRegister() {
  const key = getPoolKey();
  if (!key) {
    currentRequestedKey.value = null;
    return;
  }

  loading.value = true;
  currentRequestedKey.value = key;
  try {
    const params = route.params as { slug?: string; id?: string };
    let app: AppRecord | null = null;

    if (params.slug) {
      const { data } = await http.get<AppRecord>(
        `/api/sidenav-items/slug/${params.slug}`
      );
      app = data;
    } else if (params.id) {
      const { data } = await http.get<AppRecord>(
        `/api/sidenav-items/${params.id}`
      );
      app = data;
    }

    if (app?.iframe_url) {
      orchestrator.ensureEmbedded(
        key,
        appendParentOrigin(
          rewriteIframeUrlForLocalDev(app.iframe_url)
        ),
        app.label || ""
      );
      currentRequestedKey.value = null;
    } else {
      console.warn("No iframe_url found for this app.");
      orchestrator.evictByKey(key);
      orchestrator.setActiveEmbeddedKey(null);
    }
  } catch (err) {
    console.error("Failed to load app", err);
    orchestrator.evictByKey(key);
    orchestrator.setActiveEmbeddedKey(null);
  } finally {
    loading.value = false;
  }
}

function maybeLoadAndRegister() {
  if (isEmbeddedRoute()) {
    loadAndRegister();
  } else {
    currentRequestedKey.value = null;
    orchestrator.clearActiveEmbedded();
  }
}

onMounted(maybeLoadAndRegister);
watch(
  () => [route.name, route.params.slug, route.params.id],
  maybeLoadAndRegister
);
</script>

<style scoped>
.embedded-pool {
  position: relative;
  min-height: 100%;
}

.embedded-iframe-wrapper {
  min-height: 100%;
}

.embedded-iframe {
  display: block;
  min-height: 100%;
}

/* Full-screen: no top padding so iframe uses full viewport; exit pill floats above */
.embedded-pool--fullscreen {
  padding: 0;
  height: 100vh;
}
</style>
